// src/hooks/useAllAttributions.ts
import { useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAttributionByChain } from 'src/utils/fetchAttributionByChain';
import { TBlockchain } from 'src/types';

type BitcoinTxWithAddrs = {
  cpin: { addr: string }[];
  cpout: { addr: string }[];
};
type EVMTxWithAddrs = { _from: string; _to: string };

function isBitcoinTx(
  tx: BitcoinTxWithAddrs | EVMTxWithAddrs,
): tx is BitcoinTxWithAddrs {
  return 'cpin' in tx && 'cpout' in tx;
}

export function useAllAttributions(
  chain: TBlockchain,
  txs: BitcoinTxWithAddrs[] | EVMTxWithAddrs[] = [],
  additionalAddresses: string[] = [],
) {
  const queryClient = useQueryClient();
  const allAddresses = useMemo(() => {
    const addresses = Array.from(
      new Set([
        ...getAddressesFromListofTxsDependingOnChain(chain, txs),
        ...additionalAddresses,
      ]),
    );
    // Only normalize to lowercase for EVM chains, Bitcoin addresses are case-sensitive
    return chain === 'bitcoin'
      ? addresses
      : addresses.map((addr) => addr.toLowerCase());
  }, [chain, txs, additionalAddresses]);

  // Use regular useQuery instead of useSuspenseQuery
  const { data: batchData } = useQuery({
    queryKey: ['attribution-batch', chain, allAddresses],
    queryFn: async () => {
      if (!allAddresses.length) return {};
      return await fetchAttributionByChain(chain, allAddresses);
    },
    enabled: allAddresses.length > 0,
    staleTime: Infinity,
  });

  // Populate individual cache entries when batch completes
  useEffect(() => {
    if (!batchData) return;

    Object.entries(batchData).forEach(([addr, attribution]) => {
      // Only normalize to lowercase for EVM chains, Bitcoin addresses are case-sensitive
      const normalizedAddr = chain === 'bitcoin' ? addr : addr.toLowerCase();
      // skip true "undefined"
      if (attribution === undefined) {
        // optional: remember "we looked but got nothing"
        queryClient.setQueryData(['attribution', chain, normalizedAddr], null);
      } else {
        queryClient.setQueryData(
          ['attribution', chain, normalizedAddr],
          attribution,
        );
      }
    });

    // Invalidate attribution quota since we just consumed some quota
    // Only invalidate if we actually fetched attributions (not empty result)
    if (Object.keys(batchData).length > 0) {
      void queryClient.invalidateQueries({
        queryKey: ['attributionQuota'],
      });
    }
  }, [batchData, chain, queryClient]);

  return { data: batchData ?? {} };
}

const getAddressesFromListofTxsDependingOnChain = (
  chain: TBlockchain,
  txs: BitcoinTxWithAddrs[] | EVMTxWithAddrs[],
) => {
  if (chain === 'bitcoin') {
    return txs.flatMap((tx) => {
      if (isBitcoinTx(tx)) {
        return [...tx.cpin, ...tx.cpout].map((io) => io.addr);
      }
      return [];
    });
  } else {
    const addresses: string[] = [];
    for (const tx of txs) {
      if (!isBitcoinTx(tx)) {
        addresses.push(tx._from, tx._to);
      }
    }
    return addresses;
  }
};
